<!--
  This is for finding a restaurant
  selected restaurant is checked against our internal database
  options are then given to add if not there or use if it is.

-->

<script setup>
import { onMounted, ref, computed, watch } from 'vue'
import { NOTE_MAX_LENGTH } from '@/utils/profileNotes'
import { useDisplay } from 'vuetify'
import { mediaUrl } from '@core/utils/mediaUrl'
import {useReservationsStore, useRestaurantsStore, useUserGroupsStore, useUsersStore} from "@/stores"
import axios from "@axios"
import lodash, { max } from "underscore"
import { useRouter } from 'vue-router'
import FriendsLikedChips from '@core/components/FriendsLikedChips.vue'
import RestaurantTitleBlock from '@core/components/RestaurantTitleBlock.vue'
import GooglePlacesService from '@/services/GooglePlacesService'

const props = defineProps({
  openMenuInitially: {
    type: Boolean,
    default: true
  },
  dontShowInitialRestaurants: {
    type: Boolean,
    default: false
  },
  claimMode: {
    type: Boolean,
    default: false
  },
  searchPrompt: {
    type: String,
    default: "Restaurants"
  },
  displayOnMasthead: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits([
  'onSearchComplete',
  'onFocusChange',
])

const router = useRouter()

const isFocused = ref(false)

// Desktop: no explicit width so the results panel anchors to the search
// box itself (Vuetify sizes a connected menu to its activator). Mobile
// keeps the full-width sheet.
const { smAndDown } = useDisplay()

const searchMenuProps = computed(() => {
  // No height of our own. `height: '100%'` + `minHeight: '50%'` resolved
  // against the overlay's container rather than the viewport, pinning the panel
  // to a height unrelated to its content — short lists left dead space and long
  // ones were cut off with no sign there was more (Kapil, 8/31: "the dropdown
  // doesn't go down all the way"). Left alone, Vuetify sizes a connected menu
  // to its content and caps it to the space below the activator, which is the
  // behaviour we actually want.
  // A NUMBER, in pixels. Vuetify coerces these menu dimensions with parseInt,
  // so any CSS unit is silently dropped: the previous `height: '100%'` became
  // a 100px-tall panel and `minHeight: '50%'` a 50px one — which is exactly
  // what "the dropdown doesn't go down all the way" was (Kapil, 8/31). A
  // percentage or vh string looks right and is not; '70vh' measured as 70px.
  // maxHeight (not height) so the panel still ends where the results end.
  const base = {
    closeOnContentClick: true,
    maxHeight: 640,
    contentClass: 'main-search-menu',
    // A light scrim so the page behind recedes without disappearing — the
    // panel is white on a near-white page, and an edge alone leaves the two
    // reading as one surface. Deliberately faint: being able to still see the
    // home screen is the point of a dropdown over a takeover.
    scrim: 'rgba(15, 51, 48, 0.28)',
  }

  return smAndDown.value ? { ...base, width: '100%' } : base
})

const userGroupsStore = useUserGroupsStore()

const usersStore = useUsersStore()
let myUser = null
let loginDialogMode = null;
({ myUser, loginDialogMode } = storeToRefs(usersStore))

const restaurantsStore = useRestaurantsStore()
let restaurants = null
let bookedRestaurantsSearch = null
let restaurantSearchResults = null
let favoriteNotes = null;
({ restaurants, bookedRestaurantsSearch, restaurantSearchResults, favoriteNotes } = storeToRefs(restaurantsStore))

// Helper to get favorite note for a restaurant item
const getRestaurantNote = item => {
  const id = item?._id || item?.id
  
  return id ? favoriteNotes.value[id]?.note : null
}

// Helper to check if restaurant is favorited by current user
const isRestaurantFavorited = item => {
  if (!myUser.value?._id) return false
  if (item?.isFavorited) return true
  const favoritedBy = item?.favoritedBy
  if (favoritedBy && Array.isArray(favoritedBy)) {
    return favoritedBy.some(id => id?.toString() === myUser.value._id?.toString())
  }
  
  return false
}

// Note dialog state
const noteDialogOpen = ref(false)
const noteDialogText = ref('')
const noteDialogRestaurantId = ref(null)
const noteDialogRestaurantName = ref('')
const savingNoteDialog = ref(false)

const openNoteDialog = item => {
  const id = item?._id || item?.id
  noteDialogRestaurantId.value = id
  noteDialogRestaurantName.value = item?.name || ''
  noteDialogText.value = favoriteNotes.value[id]?.note || ''
  noteDialogOpen.value = true
}

const noteDialogError = ref('')

const saveNoteDialog = async () => {
  savingNoteDialog.value = true
  noteDialogError.value = ''

  const res = await restaurantsStore.saveFavoriteNote(noteDialogRestaurantId.value, noteDialogText.value)

  savingNoteDialog.value = false
  if (res && res.ok === false) {
    noteDialogError.value = res.message

    return
  }
  noteDialogOpen.value = false
}

const cancelNoteDialog = () => {
  noteDialogOpen.value = false
}

const reservationsStore = useReservationsStore()

const selectedRestaurant = ref()
const previousResults = ref([])
const dialog = ref(usersStore.showingMainSearch)

const fetchingAutocomplete = ref(false)
const hasSearched = ref(false)

const selectedRestaurantObject = ref(null)
const isMissingDialogOpen = ref(false)
const isAddRestaurantDialogOpen = ref(false)

// Cache for timezone data (used in newDraft for new restaurants)
const timezoneCache = new Map()

// Google Places service instance
const googlePlacesService = new GooglePlacesService()

// Cache for fetched Google Photos - reactive object for googlePlaceId -> photoUrl
const fetchedGooglePhotos = ref({})
const fetchingGooglePhotos = new Set()

// Track failed Google photo loads to skip cached URL and force a fresh fetch
const failedGooglePhotoIds = ref({})

// Helper to get Google Places photo URL
const getGooglePhotoUrl = (photoReference, maxWidth = 200, maxHeight = 200) => {
  const apiKey = import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY
  
  return `https://d128sk95sboo22.cloudfront.net/maps/api/place/photo?maxwidth=${maxWidth}&maxheight=${maxHeight}&photo_reference=${photoReference}&key=${apiKey}`
}

// Helper functions to check image availability for a restaurant item
const hasLocalImages = item => {
  const imageIds = item.imageIds ?? item.raw?.imageIds
  if (imageIds && imageIds.length > 0) return true

  const eventSpaces = item.eventSpaces ?? item.raw?.eventSpaces
  if (eventSpaces?.length > 0) {
    if (eventSpaces.some(space => space.imageId)) return true
  }

  return false
}

const hasCachedGooglePhotos = item => {
  const googlePlaceDetails = item.googlePlaceDetails ?? item.raw?.googlePlaceDetails
  
  return googlePlaceDetails?.photos?.length > 0 && googlePlaceDetails.photos[0].photo_reference
}

const canFetchGooglePhotos = item => {
  const googlePlaceId = item.googlePlaceId ?? item.raw?.googlePlaceId
  
  return googlePlaceId && !hasLocalImages(item) && !hasCachedGooglePhotos(item)
}

// Get restaurant image URL with fallback logic
const getRestaurantImageUrl = item => {
  const imageIds = item.imageIds ?? item.raw?.imageIds

  // 1. Use uploaded images from database
  if (imageIds?.length) {
    return mediaUrl(imageIds[0], 'thumb')
  }

  // 2. Fallback to event spaces images
  const eventSpaces = item.eventSpaces ?? item.raw?.eventSpaces
  if (eventSpaces?.length > 0) {
    const spaceWithImage = eventSpaces.find(space => space.imageId)
    if (spaceWithImage) {
      return mediaUrl(spaceWithImage.imageId, 'thumb')
    }
  }

  const googlePlaceId = item.googlePlaceId ?? item.raw?.googlePlaceId

  // 3. Fallback to cached Google Places photos (skip if previously failed to load)
  const googlePlaceDetails = item.googlePlaceDetails ?? item.raw?.googlePlaceDetails
  if (!failedGooglePhotoIds.value[googlePlaceId] && googlePlaceDetails?.photos?.length > 0) {
    const photoRef = googlePlaceDetails.photos[0].photo_reference
    if (photoRef) {
      return getGooglePhotoUrl(photoRef)
    }
  }

  // 4. Fallback to on-demand fetched Google Places photo
  if (googlePlaceId && fetchedGooglePhotos.value[googlePlaceId]) {
    return fetchedGooglePhotos.value[googlePlaceId]
  }

  return null
}

// Handle image load failure - attempt fresh Google Places fetch
const onImageError = item => {
  const googlePlaceId = item?.googlePlaceId ?? item?.raw?.googlePlaceId
  if (googlePlaceId && !fetchedGooglePhotos.value[googlePlaceId]) {
    failedGooglePhotoIds.value[googlePlaceId] = true
    fetchGooglePlacePhoto(item, { force: true })
  }
}

// First letter of restaurant name for image placeholder
const getRestaurantInitial = item => {
  const name = item?.name ?? item?.raw?.name
  
  return name ? name.charAt(0).toUpperCase() : '?'
}

// Deterministic background color based on restaurant name
const getInitialBgColor = item => {
  const colors = [
    '#3F998F',
    '#E8927C',
    '#7C9EB2',
    '#B8A9C9',
    '#8DB580',
    '#D4A574',
    '#6B8E9B',
    '#C4847C',
  ]
  const name = item?.name ?? item?.raw?.name ?? ''
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  
  return colors[Math.abs(hash) % colors.length]
}

// Get booked text for a restaurant item (matches RestaurantGridCard pattern)
const getBookedText = item => {
  const raw = item?.raw ?? item
  const bookingCount = raw?.bookingCount
  const lastBookingPeriod = raw?.lastBookingPeriod
  if (!bookingCount || !lastBookingPeriod || lastBookingPeriod === 'never') return ''

  let timeString = lastBookingPeriod
  if (bookingCount >= 3 && lastBookingPeriod !== 'last 1yr+') {
    timeString = `incl ${lastBookingPeriod}`
  }

  if (bookingCount >= 100) return `100+ times, ${timeString}`
  if (bookingCount >= 50) return `50+ times, ${timeString}`
  if (bookingCount >= 20) return `20+ times, ${timeString}`
  if (bookingCount >= 10) return `10+ times, ${timeString}`
  if (bookingCount >= 3) return `3+ times, ${timeString}`
  
  return `in ${timeString}`
}

// Get restaurant ID for FriendsLikedChips
const getRestaurantId = item => {
  const raw = item?.raw ?? item
  
  return raw?._id || raw?.id
}

// Fetch Google Places photo on-demand
const fetchGooglePlacePhoto = async (item, { force = false } = {}) => {
  const googlePlaceId = item.googlePlaceId ?? item.raw?.googlePlaceId

  if (!googlePlaceId) return
  if (fetchingGooglePhotos.has(googlePlaceId)) return
  if (fetchedGooglePhotos.value[googlePlaceId]) return
  if (!force && !canFetchGooglePhotos(item)) return

  fetchingGooglePhotos.add(googlePlaceId)
  try {
    const placeDetails = await googlePlacesService.getPlaceDetails(googlePlaceId, ['photos'])
    if (placeDetails?.photos?.length > 0) {
      const photoRef = placeDetails.photos[0].photo_reference
      if (photoRef) {
        fetchedGooglePhotos.value[googlePlaceId] = getGooglePhotoUrl(photoRef)
      }
    }
  } catch (error) {
    console.error('Failed to fetch Google Places photo:', error)
  } finally {
    fetchingGooglePhotos.delete(googlePlaceId)
  }
}

// Check if restaurant has any image available
const hasRestaurantImage = item => {
  if (hasLocalImages(item)) return true
  if (hasCachedGooglePhotos(item)) return true

  const googlePlaceId = item.googlePlaceId ?? item.raw?.googlePlaceId
  if (googlePlaceId && fetchedGooglePhotos.value[googlePlaceId]) return true

  // Show image container if we can fetch (will show loading state)
  if (canFetchGooglePhotos(item)) return true

  return false
}

// Debounced Autocomplete Fetch
const fetchAutoComplete = lodash.debounce(async input => {
  if (!input || input.trim().length < 2) {
    hasSearched.value = false

    return
  }
  fetchingAutocomplete.value = true
  hasSearched.value = false

  // Fetch internal restaurant search
  await restaurantsStore.searchRestaurants(input)

  fetchingAutocomplete.value = false
  hasSearched.value = true
}, 300)

// Sort Preferred Restaurants
function sortPreferredRestaurants(a, b) {
  if (a.status === 'Preferred' && b.status === 'Member Favorite') return -1
  if (a.status === 'Member Favorite' && b.status === 'Preferred') return 1
  
  return (b.favoriteCount || b.favoritedBy?.length || 0) - (a.favoriteCount || a.favoritedBy?.length || 0)
}

// Remove Duplicate Entries by Google Place ID
function removeDuplicatesById(array) {
  const seen = new Set()
  
  return array.filter(el => {
    const isDuplicate = seen.has(el.googlePlaceId)
    seen.add(el.googlePlaceId)
    
    return !isDuplicate
  })
}

/**
 * Merges results from MongoDB search (simplified to match EditReservationDialog)
 */
const mergedSearchResults = computed(() => {
  const searchTerm = selectedRestaurant.value?.trim().toLowerCase()

  if (!searchTerm || searchTerm.length < 2) {
    return []
  }

  // Return MongoDB search results directly (already sorted and filtered by backend)
  // These come from restaurantsStore.searchRestaurants() which uses the optimized /restaurant/ endpoint
  return restaurantSearchResults.value || []
})

// Custom filter that strips diacritics and apostrophes so "cafe" matches "Café", "Gibsons" matches "Gibson's", etc.
const normalizedFilter = (itemTitle, queryText) => {
  if (!queryText) return true
  const normalize = s => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/['\u2018\u2019\u02bc]/g, '').toLowerCase()

  return normalize(itemTitle).includes(normalize(queryText))
}

const preSearchResults = computed(() => {
  console.log('bookedRestaurantsSearch.value', bookedRestaurantsSearch.value)
  if (!props.dontShowInitialRestaurants) {
    return bookedRestaurantsSearch.value
  }
  
  return []
})

const showDialog = () => {
  isMissingDialogOpen.value = true
}

const handleRestaurantAdded = async restaurantData => {
  console.log('Restaurant added:', restaurantData)
  
  // Refresh restaurants list
  await restaurantsStore.getRestaurants()
  
  // Clear search input
  selectedRestaurant.value = null
}

// Handle mobile result click without setting VTextField value (avoids "[object Object]" flash)
const handleMobileResultClick = item => {
  const restaurant = item.raw || item

  if (restaurant.profileId && restaurant.status !== 'Unlisted') {
    document.activeElement.blur()
    emit('onSearchComplete', restaurant)
    dialog.value = false
    router.push({ path: '/r/' + restaurant.profileId })
    
    return
  }

  if (restaurant.googlePlaceId && restaurants.value.find(x => x.googlePlaceId == restaurant.googlePlaceId)) {
    const matched = restaurants.value.find(x => x.googlePlaceId == restaurant.googlePlaceId)
    if (matched.status !== 'Unlisted') {
      emit('onSearchComplete', matched)
      dialog.value = false
      router.push({ path: '/r/' + matched.profileId })
      
      return
    }
    
    return
  }

  // Fallback: set selectedRestaurant for watcher to handle
  selectedRestaurant.value = restaurant
}

// Per-result RestaurantHeart refs (keyed by restaurant id) so the
// "Favorited by" chips can open that restaurant's Fans drawer.
const searchHeartRefs = {}
const setSearchHeartRef = (id, el) => {
  if (!id) return
  if (el) searchHeartRefs[id] = el
  else delete searchHeartRefs[id]
}
const openFansForRestaurant = id => searchHeartRefs[id]?.openFavoritedBy()

const handleAddFavoriteClick = () => {
  // Check if user is logged in (not a dummy user and not null)
  if (!myUser.value || myUser.value.userLevel === 'dummy') {
    // User is not logged in, open request to join dialog
    loginDialogMode.value = 'activate'
  } else {
    // User is logged in, open add restaurant dialog
    isAddRestaurantDialogOpen.value = true
  }
}

/**
 * Creates a new draft reservation for the selected restaurant.
 * If the user is an admin or adminStandard of a group, a dialog
 * is shown to choose the member making the request. Otherwise, a new dummy user
 * is created and the request is made on their behalf.
 *
 * @param {object} item - The selected restaurant object
 * @returns {Promise<void>}
 */

const newDraft = async item => {
  console.log(item)

  const selectedItem = item.raw || item

  // All restaurants should already exist in MongoDB from search results
  const restaurant = selectedItem

  if (restaurant && restaurant.status === 'Unlisted') {
    selectedRestaurantObject.value = selectedItem
    showDialog()
    
    return
  }

  if (!myUser.value) {
    await usersStore.loginNewDummyUser()
  }
  await usersStore.startNewReservation({ restaurant: restaurant.name, market: restaurant.market })
  usersStore.closeMainSearch()
}

// Fetch friends-favorited data for a list of restaurant IDs (non-blocking)
const fetchFriendsDataForIds = ids => {
  if (!myUser.value || myUser.value.userLevel === 'dummy') return
  if (ids?.length) {
    restaurantsStore.fetchFriendsFavoritedBatch(ids)
  }
}

// Watch merged search results and fetch Google Places photos for items that need them
watch(
  () => mergedSearchResults.value,
  async newResults => {
    if (!newResults || newResults.length === 0) return

    // Fetch Google Places photos for all items that can fetch them
    newResults.forEach(item => {
      if (canFetchGooglePhotos(item)) {
        fetchGooglePlacePhoto(item)
      }
    })

    // Fetch friends-favorited data for search results
    const ids = newResults.map(item => item._id || item.id).filter(Boolean)
    fetchFriendsDataForIds(ids)
  },
  { immediate: true }
)

// Watch pre-search results and fetch Google Places photos for items that need them
watch(
  () => preSearchResults.value,
  async newResults => {
    if (!newResults || newResults.length === 0) return

    // Fetch Google Places photos for all items that can fetch them
    newResults.forEach(item => {
      if (canFetchGooglePhotos(item.raw)) {
        fetchGooglePlacePhoto(item.raw)
      }
    })

    // Fetch friends-favorited data for pre-search results
    const ids = newResults.map(item => item.raw?._id || item.raw?.id).filter(Boolean)
    fetchFriendsDataForIds(ids)
  },
  { immediate: true }
)

watch(
  () => selectedRestaurant?.value,
  async (value, oldValue, onCleanup) => {
    // Handle restaurant selection and navigation
    if(selectedRestaurant?.value && typeof selectedRestaurant?.value == 'object'){
      let restaurant = selectedRestaurant?.value

      // If it has a profileId, navigate to the profile (unless it's unlisted)
      if(selectedRestaurant?.value?.profileId){
        // Prevent navigation to unlisted restaurants
        if(selectedRestaurant?.value?.status === 'Unlisted'){
          console.log('Cannot navigate to unlisted restaurant profile')
          selectedRestaurant.value = null
          
          return
        }

        document.activeElement.blur()
        emit('onSearchComplete', selectedRestaurant.value)
        router.push({ path: '/r/'+selectedRestaurant?.value.profileId }).then(() => {
          selectedRestaurant.value = null
        })
        
        return
      }

      // Check if restaurant with matching googlePlaceId is already in restaurants.value
      if(restaurant.googlePlaceId && restaurants.value.find(x => x.googlePlaceId == restaurant.googlePlaceId)){
        selectedRestaurant.value = restaurants.value.find(x => x.googlePlaceId == restaurant.googlePlaceId)

        // Prevent navigation to unlisted restaurants
        if(selectedRestaurant?.value?.status === 'Unlisted'){
          console.log('Cannot navigate to unlisted restaurant profile')
          selectedRestaurant.value = null
          
          return
        }

        emit('onSearchComplete', selectedRestaurant.value)
        router.push({ path: '/r/'+selectedRestaurant?.value.profileId }).then(() => {
          selectedRestaurant.value = null
        })
        
        return
      }

      // Market navigation (not currently used)
      if(selectedRestaurant?.value?.isMarket){
        document.activeElement.blur()
        emit('onSearchComplete', selectedRestaurant.value)
        router.push({ path: `/discover/all/${selectedRestaurant?.value?.market.toLowerCase().replace(', ','-')}` }).then(() => {
          selectedRestaurant.value = null
        })
        
        return
      }

      // If we have an ID, fetch fresh data from backend
      if(restaurant.id && restaurant._id){
        await usersStore.showLoadingOverlay()
        await restaurantsStore.getRestaurantById(restaurant.id)
        await usersStore.hideLoadingOverlay()
      }
    }
  },{deep: true}
)

onMounted(async () => {
  await restaurantsStore.getDefaultSearchData(20)
})
</script>

<template>
  <div>
    <RestaurantMissingDialog
      v-if="selectedRestaurantObject"
      v-model:is-dialog-visible="isMissingDialogOpen"
      :restaurant="selectedRestaurantObject"
      @on-notification-sent="usersStore.closeMainSearch()"
    />

    <!-- Note Editor Dialog -->
    <VDialog
      v-model="noteDialogOpen"
      max-width="420"
      @click:outside="cancelNoteDialog"
    >
      <VCard class="note-dialog-card">
        <div class="note-dialog-header">
          <VIcon
            icon="mdi-note-text-outline"
            size="20"
            color="primary"
          />
          <span class="note-dialog-title">Store notes for {{ noteDialogRestaurantName }}</span>
        </div>
        <VCardText class="note-dialog-body">
          <VTextarea
            v-model="noteDialogText"
            placeholder="Favorite dishes, tables, team members, occasions, who recommended..."
            :counter="NOTE_MAX_LENGTH"
            :maxlength="NOTE_MAX_LENGTH"
            rows="3"
            auto-grow
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="note-textarea"
          />
          <p
            v-if="noteDialogError"
            class="text-error text-caption mt-1 mb-0"
          >
            {{ noteDialogError }}
          </p>
        </VCardText>
        <VCardActions class="note-dialog-actions">
          <VSpacer />
          <VBtn
            variant="text"
            size="small"
            @click="cancelNoteDialog"
          >
            Cancel
          </VBtn>
          <VBtn
            color="primary"
            variant="flat"
            size="small"
            rounded="lg"
            :loading="savingNoteDialog"
            @click="saveNoteDialog"
          >
            Save Note
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <AddRestaurantDialog
      v-model:is-dialog-visible="isAddRestaurantDialogOpen"
      :search-query="selectedRestaurant"
      @on-restaurant-added="handleRestaurantAdded"
    />

    <!-- Desktop search bar with expand functionality -->
    <div
      v-if="!$vuetify.display.mdAndDown"
      style="width: 100%;"
    >
      <VCombobox
        v-model="selectedRestaurant"
        label=""
        :items="mergedSearchResults"
        :loading="fetchingAutocomplete"
        :menu-props="searchMenuProps"
        :hide-no-data="!!selectedRestaurant"
        :custom-filter="normalizedFilter"
        item-title="name"
        item-value="displayNameAndMarket"
        :return-object="true"
        persistent-clear
        placeholder="Search for favorite restaurant"
        clearable
        style="width: 100%;"
        class="small-label-search desktop-search-field"
        variant="solo-filled"
        flat
        rounded="pill"
        density="comfortable"
        hide-details
        bg-color="grey-lighten-3"
        @update:model-value="changesMade = true;"
        @update:search="fetchAutoComplete"
        @focus="isFocused = true; emit('onFocusChange', true);"
        @blur="isFocused = false; emit('onFocusChange', false);"
      >
        <template #prepend-inner>
          <VIcon>mdi-magnify</VIcon>
        </template>
        <template
          v-if="dontShowInitialRestaurants"
          #no-data
        >
          <VListItem>
            <VListItemTitle class="custom-title">
              Search by restaurant name
            </VListItemTitle>
          </VListItem>
        </template>
        <!-- DESKTOP SEARCH RESULTS -->
        <template
          v-else
          #no-data
        >
          <VListItem>
            <div class="mt-4">
              <VListItemTitle class="custom-title text-caption font-weight-bold">
                Booked by Members
              </VListItemTitle>
            </div>
          </VListItem>
          <div
            v-for="item in preSearchResults"
            :key="item.raw.id"
            class="search-dropdown-card"
            @click="selectedRestaurant = item.raw;"
          >
            <VBtn
              class="search-dropdown-add-btn"
              icon
              size="small"
              density="compact"
              aria-label="Add to request"
              @click.stop="newDraft(item);"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <ellipse
                  cx="8"
                  cy="8"
                  rx="8"
                  ry="8"
                  fill="var(--ds-brand-primary-dark)"
                />
                <path
                  d="M10.5261 7.99958H5.05243M7.78927 5.47327V10.5259"
                  stroke="white"
                  stroke-width="0.860438"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </VBtn>

            <div class="search-dropdown-content">
              <div class="search-dropdown-text-wrapper">
                <RestaurantTitleBlock
                  :ref="el => setSearchHeartRef(getRestaurantId(item.raw), el)"
                  :restaurant="item.raw"
                  :name="item.raw.name"
                  :market="item.raw.market"
                  :group="item.raw.group || ''"
                  :status="item.raw.status || ''"
                  compact
                />
                <div class="search-dropdown-subtitle-wrapper">
                  <p
                    v-if="item.raw.market"
                    class="search-dropdown-subtitle"
                  >
                    {{ item.raw.market }}
                  </p>
                  <p
                    v-if="getBookedText(item.raw)"
                    class="search-dropdown-subtitle search-dropdown-booked-text"
                  >
                    <VIcon
                      icon="mdi-check"
                      size="14"
                      style="vertical-align: middle; margin-right: 2px;"
                    />
                    Booked {{ getBookedText(item.raw) }}
                  </p>
                  <div
                    v-if="myUser && isRestaurantFavorited(item.raw)"
                    class="search-dropdown-note-row"
                    @click.stop="openNoteDialog(item.raw)"
                  >
                    <VIcon
                      size="14"
                      :icon="getRestaurantNote(item.raw) ? 'mdi-note-text' : 'mdi-note-text-outline'"
                      :color="getRestaurantNote(item.raw) ? 'primary' : 'grey'"
                      class="search-dropdown-note-icon"
                    />
                    <span
                      v-if="getRestaurantNote(item.raw)"
                      class="search-dropdown-note"
                    >{{ getRestaurantNote(item.raw) }}</span>
                  </div>
                  <FriendsLikedChips
                    :restaurant-id="getRestaurantId(item.raw)"
                    @click="openFansForRestaurant(getRestaurantId(item.raw))"
                  />
                </div>
              </div>
            </div>

            <div class="search-dropdown-image-container">
              <VImg
                v-if="hasRestaurantImage(item.raw) && getRestaurantImageUrl(item.raw)"
                :src="getRestaurantImageUrl(item.raw)"
                class="restaurant-image rounded-lg"
                cover
                height="86"
                width="86"
                @error="onImageError(item.raw)"
              >
                <template #placeholder>
                  <VRow
                    class="fill-height ma-0"
                    align="center"
                    justify="center"
                  >
                    <VProgressCircular
                      indeterminate
                      color="grey-lighten-5"
                      size="24"
                    />
                  </VRow>
                </template>
                <template #error>
                  <div
                    class="image-initial-placeholder"
                    :style="{ backgroundColor: getInitialBgColor(item.raw) }"
                  >
                    <span class="initial-letter">{{ getRestaurantInitial(item.raw) }}</span>
                  </div>
                </template>
              </VImg>
              <div
                v-else-if="canFetchGooglePhotos(item.raw)"
                class="image-placeholder"
              >
                <VRow
                  class="fill-height ma-0"
                  align="center"
                  justify="center"
                >
                  <VProgressCircular
                    indeterminate
                    color="grey-lighten-5"
                    size="24"
                  />
                </VRow>
              </div>
              <div
                v-else
                class="image-initial-placeholder"
                :style="{ backgroundColor: getInitialBgColor(item.raw) }"
              >
                <span class="initial-letter">{{ getRestaurantInitial(item.raw) }}</span>
              </div>
            </div>
          </div>
        </template>
        <template #item="{ item }">
          <div
            class="search-dropdown-card"
            @click="selectedRestaurant = item.raw;"
          >
            <VBtn
              class="search-dropdown-add-btn"
              icon
              size="small"
              density="compact"
              aria-label="Add to request"
              @click.stop="newDraft(item);"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <ellipse
                  cx="8"
                  cy="8"
                  rx="8"
                  ry="8"
                  fill="var(--ds-brand-primary-dark)"
                />
                <path
                  d="M10.5261 7.99958H5.05243M7.78927 5.47327V10.5259"
                  stroke="white"
                  stroke-width="0.860438"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </VBtn>

            <div class="search-dropdown-content">
              <div class="search-dropdown-text-wrapper">
                <RestaurantTitleBlock
                  :ref="el => setSearchHeartRef(getRestaurantId(item.raw), el)"
                  :restaurant="item.raw"
                  :name="item.raw.name"
                  :market="item.raw.market"
                  :group="item.raw.group || ''"
                  :status="item.raw.status || ''"
                  compact
                />
                <div class="search-dropdown-subtitle-wrapper">
                  <p
                    v-if="item.raw.market"
                    class="search-dropdown-subtitle"
                  >
                    {{ item.raw.market }}
                  </p>
                  <p
                    v-if="getBookedText(item.raw)"
                    class="search-dropdown-subtitle search-dropdown-booked-text"
                  >
                    <VIcon
                      icon="mdi-check"
                      size="14"
                      style="vertical-align: middle; margin-right: 2px;"
                    />
                    Booked {{ getBookedText(item.raw) }}
                  </p>
                  <div
                    v-if="myUser && isRestaurantFavorited(item.raw)"
                    class="search-dropdown-note-row"
                    @click.stop="openNoteDialog(item.raw)"
                  >
                    <VIcon
                      size="14"
                      :icon="getRestaurantNote(item.raw) ? 'mdi-note-text' : 'mdi-note-text-outline'"
                      :color="getRestaurantNote(item.raw) ? 'primary' : 'grey'"
                      class="search-dropdown-note-icon"
                    />
                    <span
                      v-if="getRestaurantNote(item.raw)"
                      class="search-dropdown-note"
                    >{{ getRestaurantNote(item.raw) }}</span>
                  </div>
                  <FriendsLikedChips
                    :restaurant-id="getRestaurantId(item.raw)"
                    @click="openFansForRestaurant(getRestaurantId(item.raw))"
                  />
                </div>
              </div>
            </div>

            <div class="search-dropdown-image-container">
              <VImg
                v-if="hasRestaurantImage(item.raw) && getRestaurantImageUrl(item.raw)"
                :src="getRestaurantImageUrl(item.raw)"
                class="restaurant-image rounded-lg"
                cover
                height="86"
                width="86"
                @error="onImageError(item.raw)"
              >
                <template #placeholder>
                  <VRow
                    class="fill-height ma-0"
                    align="center"
                    justify="center"
                  >
                    <VProgressCircular
                      indeterminate
                      color="grey-lighten-5"
                      size="24"
                    />
                  </VRow>
                </template>
                <template #error>
                  <div
                    class="image-initial-placeholder"
                    :style="{ backgroundColor: getInitialBgColor(item.raw) }"
                  >
                    <span class="initial-letter">{{ getRestaurantInitial(item.raw) }}</span>
                  </div>
                </template>
              </VImg>
              <div
                v-else-if="canFetchGooglePhotos(item.raw)"
                class="image-placeholder"
              >
                <VRow
                  class="fill-height ma-0"
                  align="center"
                  justify="center"
                >
                  <VProgressCircular
                    indeterminate
                    color="grey-lighten-5"
                    size="24"
                  />
                </VRow>
              </div>
              <div
                v-else
                class="image-initial-placeholder"
                :style="{ backgroundColor: getInitialBgColor(item.raw) }"
              >
                <span class="initial-letter">{{ getRestaurantInitial(item.raw) }}</span>
              </div>
            </div>
          </div>
        </template>
        <!-- Add favorite button - always shown when user types 2+ characters -->
        <template
          v-if="selectedRestaurant && typeof selectedRestaurant === 'string' && selectedRestaurant.length >= 2"
          #append-item
        >
          <div class="add-favorite-section">
            <div
              v-if="fetchingAutocomplete"
              class="search-loading-state"
            >
              <VProgressCircular
                indeterminate
                color="primary"
                size="40"
              />
              <p class="loading-text">
                Searching restaurants...
              </p>
            </div>
            <p class="empty-state-text">
              Favorite not coming up?
            </p>
            <button
              class="add-favorite-btn"
              @click="handleAddFavoriteClick"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 3.5V12.5M3.5 8H12.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
              Add favorite
            </button>
          </div>
        </template>
      </VCombobox>
    </div>

    <!-- Mobile search bar - clicking opens fullscreen dialog -->
    <div
      v-else
      style="width: 100%;"
    >
      <VTextField
        placeholder="Search for favorite restaurant"
        prepend-inner-icon="mdi-magnify"
        readonly
        hide-details
        variant="solo-filled"
        flat
        rounded="pill"
        density="comfortable"
        bg-color="grey-lighten-3"
        style="width: 100%; cursor: pointer;"
        class="mobile-search-trigger"
        @click="dialog = true"
      />
    </div>

    <!-- Mobile fullscreen dialog -->
    <VDialog
      v-model="dialog"
      fullscreen
      hide-overlay
      transition="dialog-top-transition"
    >
      <VCard>
        <VToolbar
          color="primary"
          class="mobile-search-toolbar"
        >
          <VBtn
            color="white"
            icon
            @click="dialog = false; usersStore.closeMainSearch()"
          >
            <VIcon>mdi-arrow-left</VIcon>
          </VBtn>
          <!-- Use v-text-field instead of v-combobox for search input -->
          <VTextField
            v-model="selectedRestaurant"
            class="mobile-search-input"
            density="compact"
            placeholder="Search for favorite restaurant"
            prepend-inner-icon="mdi-magnify"
            :loading="mergingResults || fetchingAutocomplete"
            variant="plain"
            hide-details
            @update:model-value="fetchAutoComplete"
          />
        </VToolbar>
        <!-- Display search results directly in the card text -->
        <div
          v-if="mergedSearchResults.length"
          class="mobile-search-results-container"
        >
          <div
            v-for="item in mergedSearchResults"
            :key="item.id || item.googlePlaceId || item.name"
            class="search-dropdown-card mobile-search-card"
            @click="handleMobileResultClick(item)"
          >
            <VBtn
              class="search-dropdown-add-btn"
              icon
              size="small"
              density="compact"
              aria-label="Add to request"
              @click.stop="newDraft(item);"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <ellipse
                  cx="8"
                  cy="8"
                  rx="8"
                  ry="8"
                  fill="var(--ds-brand-primary-dark)"
                />
                <path
                  d="M10.5261 7.99958H5.05243M7.78927 5.47327V10.5259"
                  stroke="white"
                  stroke-width="0.860438"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </VBtn>

            <div class="search-dropdown-content">
              <div class="search-dropdown-text-wrapper">
                <RestaurantTitleBlock
                  :ref="el => setSearchHeartRef(getRestaurantId(item), el)"
                  :restaurant="item"
                  :name="item.name"
                  :market="item.market"
                  :group="item.group || ''"
                  :status="item.status || ''"
                  compact
                />
                <div class="search-dropdown-subtitle-wrapper">
                  <p
                    v-if="item.market"
                    class="search-dropdown-subtitle"
                  >
                    {{ item.market }}
                  </p>
                  <p
                    v-if="getBookedText(item)"
                    class="search-dropdown-subtitle search-dropdown-booked-text"
                  >
                    <VIcon
                      icon="mdi-check"
                      size="14"
                      style="vertical-align: middle; margin-right: 2px;"
                    />
                    Booked {{ getBookedText(item) }}
                  </p>
                  <div
                    v-if="myUser && isRestaurantFavorited(item)"
                    class="search-dropdown-note-row"
                    @click.stop="openNoteDialog(item)"
                  >
                    <VIcon
                      size="14"
                      :icon="getRestaurantNote(item) ? 'mdi-note-text' : 'mdi-note-text-outline'"
                      :color="getRestaurantNote(item) ? 'primary' : 'grey'"
                      class="search-dropdown-note-icon"
                    />
                    <span
                      v-if="getRestaurantNote(item)"
                      class="search-dropdown-note"
                    >{{ getRestaurantNote(item) }}</span>
                  </div>
                  <FriendsLikedChips
                    :restaurant-id="getRestaurantId(item)"
                    @click="openFansForRestaurant(getRestaurantId(item))"
                  />
                </div>
              </div>
            </div>

            <div class="search-dropdown-image-container">
              <VImg
                v-if="hasRestaurantImage(item) && getRestaurantImageUrl(item)"
                :src="getRestaurantImageUrl(item)"
                class="restaurant-image rounded-lg"
                cover
                height="86"
                width="86"
                @error="onImageError(item)"
              >
                <template #placeholder>
                  <VRow
                    class="fill-height ma-0"
                    align="center"
                    justify="center"
                  >
                    <VProgressCircular
                      indeterminate
                      color="grey-lighten-5"
                      size="24"
                    />
                  </VRow>
                </template>
                <template #error>
                  <div
                    class="image-initial-placeholder"
                    :style="{ backgroundColor: getInitialBgColor(item) }"
                  >
                    <span class="initial-letter">{{ getRestaurantInitial(item) }}</span>
                  </div>
                </template>
              </VImg>
              <div
                v-else-if="canFetchGooglePhotos(item)"
                class="image-placeholder"
              >
                <VRow
                  class="fill-height ma-0"
                  align="center"
                  justify="center"
                >
                  <VProgressCircular
                    indeterminate
                    color="grey-lighten-5"
                    size="24"
                  />
                </VRow>
              </div>
              <div
                v-else
                class="image-initial-placeholder"
                :style="{ backgroundColor: getInitialBgColor(item) }"
              >
                <span class="initial-letter">{{ getRestaurantInitial(item) }}</span>
              </div>
            </div>
          </div>
          <!-- Add favorite button after results -->
          <div class="mobile-add-favorite-container">
            <div class="add-favorite-section">
              <!-- Always show text -->
              <p class="empty-state-text">
                Favorite not coming up?
              </p>
              <button
                class="add-favorite-btn"
                @click="handleAddFavoriteClick"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 3.5V12.5M3.5 8H12.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                </svg>
                Add favorite
              </button>
            </div>
          </div>
        </div>
        <!-- Add favorite button - always shown when user types 2+ characters on mobile -->
        <div
          v-else-if="selectedRestaurant && typeof selectedRestaurant === 'string' && selectedRestaurant.length >= 2"
          class="mobile-add-favorite-container"
        >
          <div class="add-favorite-section">
            <div
              v-if="fetchingAutocomplete"
              class="search-loading-state"
            >
              <VProgressCircular
                indeterminate
                color="primary"
                size="50"
              />
              <p class="loading-text">
                Searching restaurants...
              </p>
            </div>
            <p class="empty-state-text">
              Favorite not coming up?
            </p>
            <button
              class="add-favorite-btn"
              @click="handleAddFavoriteClick"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 3.5V12.5M3.5 8H12.5"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
              Add favorite
            </button>
          </div>
        </div>
        <!-- Show the "Booked" section if there are no search results -->
        <div
          v-else
          class="mobile-search-results-container"
        >
          <div class="px-4 py-3">
            <h3 class="text-caption font-weight-bold">
              Booked by Members
            </h3>
          </div>
          <div
            v-for="item in preSearchResults"
            :key="item.raw.id"
            class="search-dropdown-card mobile-search-card"
            @click="handleMobileResultClick(item)"
          >
            <VBtn
              class="search-dropdown-add-btn"
              icon
              size="small"
              density="compact"
              aria-label="Add to request"
              @click.stop="newDraft(item);"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <ellipse
                  cx="8"
                  cy="8"
                  rx="8"
                  ry="8"
                  fill="var(--ds-brand-primary-dark)"
                />
                <path
                  d="M10.5261 7.99958H5.05243M7.78927 5.47327V10.5259"
                  stroke="white"
                  stroke-width="0.860438"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </VBtn>

            <div class="search-dropdown-content">
              <div class="search-dropdown-text-wrapper">
                <RestaurantTitleBlock
                  :ref="el => setSearchHeartRef(getRestaurantId(item.raw), el)"
                  :restaurant="item.raw"
                  :name="item.raw.name"
                  :market="item.raw.market"
                  :group="item.raw.group || ''"
                  :status="item.raw.status || ''"
                  compact
                />
                <div class="search-dropdown-subtitle-wrapper">
                  <p
                    v-if="item.raw.market"
                    class="search-dropdown-subtitle"
                  >
                    {{ item.raw.market }}
                  </p>
                  <p
                    v-if="getBookedText(item.raw)"
                    class="search-dropdown-subtitle search-dropdown-booked-text"
                  >
                    <VIcon
                      icon="mdi-check"
                      size="14"
                      style="vertical-align: middle; margin-right: 2px;"
                    />
                    Booked {{ getBookedText(item.raw) }}
                  </p>
                  <div
                    v-if="myUser && isRestaurantFavorited(item.raw)"
                    class="search-dropdown-note-row"
                    @click.stop="openNoteDialog(item.raw)"
                  >
                    <VIcon
                      size="14"
                      :icon="getRestaurantNote(item.raw) ? 'mdi-note-text' : 'mdi-note-text-outline'"
                      :color="getRestaurantNote(item.raw) ? 'primary' : 'grey'"
                      class="search-dropdown-note-icon"
                    />
                    <span
                      v-if="getRestaurantNote(item.raw)"
                      class="search-dropdown-note"
                    >{{ getRestaurantNote(item.raw) }}</span>
                  </div>
                  <FriendsLikedChips
                    :restaurant-id="getRestaurantId(item.raw)"
                    @click="openFansForRestaurant(getRestaurantId(item.raw))"
                  />
                </div>
              </div>
            </div>

            <div class="search-dropdown-image-container">
              <VImg
                v-if="hasRestaurantImage(item.raw) && getRestaurantImageUrl(item.raw)"
                :src="getRestaurantImageUrl(item.raw)"
                class="restaurant-image rounded-lg"
                cover
                height="86"
                width="86"
                @error="onImageError(item.raw)"
              >
                <template #placeholder>
                  <VRow
                    class="fill-height ma-0"
                    align="center"
                    justify="center"
                  >
                    <VProgressCircular
                      indeterminate
                      color="grey-lighten-5"
                      size="24"
                    />
                  </VRow>
                </template>
                <template #error>
                  <div
                    class="image-initial-placeholder"
                    :style="{ backgroundColor: getInitialBgColor(item.raw) }"
                  >
                    <span class="initial-letter">{{ getRestaurantInitial(item.raw) }}</span>
                  </div>
                </template>
              </VImg>
              <div
                v-else-if="canFetchGooglePhotos(item.raw)"
                class="image-placeholder"
              >
                <VRow
                  class="fill-height ma-0"
                  align="center"
                  justify="center"
                >
                  <VProgressCircular
                    indeterminate
                    color="grey-lighten-5"
                    size="24"
                  />
                </VRow>
              </div>
              <div
                v-else
                class="image-initial-placeholder"
                :style="{ backgroundColor: getInitialBgColor(item.raw) }"
              >
                <span class="initial-letter">{{ getRestaurantInitial(item.raw) }}</span>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VDialog>
  </div>
</template>

<style lang="scss" scoped>
.restaurant-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.restaurant-image-holder {
  width: 4rem;
  height: 4rem;
}

/* Line-separated row matching the shared restaurant card: thumbnail LEFT
   (order -1), text block, outlined "+" RIGHT (order 10) — no box */
.search-dropdown-card {
  display: flex;
  align-items: flex-start;
  padding: 15px 2px;
  gap: var(--ds-space-3);
  cursor: pointer;
  transition: background-color var(--ds-duration-normal) ease;
  border: none;
  border-bottom: 1px solid var(--ds-border-subtle);
  border-radius: 0;
  margin: 0 var(--ds-space-4);
  background: var(--ds-surface-card);
  box-sizing: border-box;
  overflow: hidden;
}

.search-dropdown-card:hover {
  background: var(--ds-surface-subtle);
}

.search-dropdown-add-btn {
  order: 10; /* the only control on the right */
  flex-shrink: 0;
  background: var(--ds-surface-card) !important;
  border: 1.5px solid var(--ds-border-strong) !important;
  border-radius: 50%;
  padding: 0 !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform var(--ds-duration-normal) ease, border-color var(--ds-duration-normal) ease;
  box-shadow: none !important;
  min-width: 30px !important;
  width: 30px !important;
  height: 30px !important;
  position: relative;
  color: var(--ds-brand-primary-dark);
  margin-top: 1px;
}

/* Quiet outlined "+": the button draws the circle; the svg keeps only its
   plus stroke (the filled ellipse goes transparent, the white plus goes
   green) */
.search-dropdown-add-btn svg {
  width: 16px;
  height: 16px;
}

.search-dropdown-add-btn svg ellipse {
  fill: transparent;
}

.search-dropdown-add-btn svg path {
  stroke: var(--ds-brand-primary-dark);
  stroke-width: 1.4;
}

.search-dropdown-add-btn:hover {
  border-color: var(--ds-brand-primary-dark) !important;
  transform: scale(1.05);
}

.search-dropdown-add-btn:active {
  transform: scale(0.95);
}

/* Text column matching the shared card — plain stacked lines, no icon rail */
.search-dropdown-content {
  flex: 1;
  min-width: 0;
  overflow: hidden; /* Prevent content overflow */
  cursor: pointer; /* Required for Safari mobile touch events */
}

/* Geometry + heart/count styling come from the global .rm-title-heart
   spec (styles.scss) */

/* Names stay brand ink on every card — the seal signals Preferred */
.text-preferred {
  color: var(--ds-brand-ink) !important;
}

.search-dropdown-heart-btn {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Favorites are the lead signal — heart + count in brand green,
     matching the shared card */
  color: var(--ds-brand-primary-dark);
  transition: color var(--ds-duration-normal) ease;
}

.search-dropdown-heart-btn :deep(.favorite-count) {
  color: var(--ds-brand-primary-dark);
}

.search-dropdown-heart-btn:hover {
  color: #E91E63;
}

.search-dropdown-subtitle-wrapper {
  grid-column: 2;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.search-dropdown-subtitle {
  font-family: var(--ds-font-body);
  font-size: 13.5px;
  font-weight: 400;
  line-height: 1.3;
  color: var(--ds-text-body);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-dropdown-note-row {
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-xs);
  color: rgba(var(--v-theme-on-surface), 0.6);
  white-space: normal;
  word-break: break-word;
  cursor: pointer;
  line-height: 1.3;
  padding-top: 5px;
  display: flex;
  align-items: center;
  gap: var(--ds-space-1);
}

.search-dropdown-note-icon {
  flex-shrink: 0;
}

.search-dropdown-note {
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-xs);
  line-height: 1.3;
  color: rgba(var(--v-theme-on-surface), 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-dropdown-booked-text {
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-xs);
  font-weight: 400;
  color: var(--ds-text-muted);
  white-space: nowrap;
  margin-left: var(--ds-space-1);
}

.image-initial-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--ds-radius-sm);
}

.initial-letter {
  font-family: var(--ds-font-heading);
  font-size: 18px;
  font-weight: var(--ds-font-medium);
  color: color-mix(in srgb, var(--ds-brand-primary-dark) 55%, var(--ds-surface-card));
  line-height: 1;
  user-select: none;
  opacity: 0.9;
}


.search-dropdown-image-container {
  order: -1; /* thumbnail leads the row */
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: var(--ds-radius-md);
  overflow: hidden;
  align-self: flex-start;
  cursor: pointer; /* Required for Safari mobile touch events */
  background: var(--ds-brand-primary-light); /* soft tile when no image */
}

.search-dropdown-image-container :deep(.v-img),
.search-dropdown-image-container .v-img {
  width: 100% !important;
  height: 100% !important;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  background-color: var(--ds-brand-primary-light);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.mobile-search-toolbar {
  :deep(.v-toolbar__content) {
    align-items: center;
  }
}

.mobile-search-input {
  margin-top: auto !important;
  margin-bottom: auto !important;
  :deep(.v-field__prepend-inner) {
    align-items: center;
  }
}

.mobile-search-results-container {
  padding: var(--ds-space-2) 0;
}

.mobile-search-card {
  margin: var(--ds-space-2) var(--ds-space-3) !important;
}

@media (max-width: 600px) {
  .search-dropdown-card {
    padding: 15px 2px;
    gap: var(--ds-space-3);
  }

  .initial-letter {
    font-size: 18px;
  }
}

@media (min-width: 960px) {
  .search-dropdown-card {
    padding: 17px 2px;
    gap: var(--ds-space-3);
  }


  .initial-letter {
    font-size: 32px;
  }
}

.google-attribution-container {
  position: fixed;
  right: 0;
  bottom: 0;
  width: 100%;
  display: flex;
  justify-content: flex-end;
}

.google-attribution {
  width: fit-content;
  margin: 0.5rem;
}


.restaurant-profile-masthead {
  position: relative;
  cursor: pointer;
}

.restaurant-profile-masthead-bkg-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;

}

.restaurant-profile-masthead-content {
  padding: 10rem 0.5rem 0.5rem;
  display: flex;
  flex-direction: row;
  align-items: end;
  justify-content: space-between;
  isolation: isolate;
  column-gap: 1rem;
  width: 100%;
  aspect-ratio: 4;
  background: linear-gradient(0deg, rgba(0,0,0,0.55) 20%, rgba(0,0,0,0) 40%, rgba(0,0,0,0)),linear-gradient(45deg, rgba(0,0,0,0.25) 20%, rgba(0,0,0,0) 50%, rgba(0,0,0,0));
}

.restaurant-profile-masthead-content-text {
  cursor: default;
}

.restaurant-profile-masthead-title {
  margin-top: 0.5rem;
  color: var(--ds-text-on-dark);
}

.restaurant-profile-masthead-info {
  color: rgba(255,255,255, 0.9);
}

:deep(.v-list-item__content .v-list-item-title) {
  display: none;
}

:deep(.custom-title) {
  display: block !important;
}

:deep(.v-list-item__prepend){
  width:16px;
}

:deep(.v-field__prepend-inner) {
  padding-left: 0px;
}

:deep(.v-field__field) {
  border-radius: inherit;
}

.small-label-search :deep(.v-field__input) {
  padding-left: 0;
}

:deep(.small-label-search .v-label) {
  font-size: var(--ds-text-base);
}

:deep(.small-label-search .v-field__append-inner) {
  display: none;
}

.search-loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--ds-space-10) var(--ds-space-5);
  text-align: center;
  min-height: 200px;
  gap: var(--ds-space-4);
}

.loading-text {
  font-size: var(--ds-text-base);
  font-weight: 400;
  color: var(--ds-text-muted);
  margin: 0;
}

.add-favorite-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--ds-space-10) var(--ds-space-5);
  text-align: center;
  min-height: 150px;
}

.mobile-add-favorite-container {
  padding: var(--ds-space-5) 0;
  border-top: 1px solid var(--ds-border-light);
}

.empty-state-text {
  font-size: var(--ds-text-md);
  font-weight: 500;
  color: var(--ds-text-muted);
  margin-bottom: var(--ds-space-4);
}

.add-favorite-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: var(--ds-space-2) var(--ds-space-4);
  background: transparent;
  border: 1px solid var(--ds-brand-primary-dark);
  border-radius: var(--ds-radius-xl);
  color: var(--ds-brand-primary-dark);
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-sm);
  font-weight: 500;
  cursor: pointer;
  transition: all var(--ds-duration-normal) ease;

  &:hover {
    background: var(--ds-brand-primary-dark);
    color: var(--ds-text-on-dark);
    transform: translateY(-1px);
    box-shadow: var(--ds-shadow-focus);
  }

  &:active {
    transform: translateY(0);
  }
}

:deep(.desktop-search-field .v-field) {
  border-radius: var(--ds-radius-full);
  background-color: var(--ds-surface-inset);
  box-shadow: none;
  border: 1px solid var(--ds-border-light);
}

:deep(.desktop-search-field .v-field--focused) {
  box-shadow: none;
  background-color: var(--ds-surface-inset);
  border-color: var(--ds-border-default);
}

:deep(.desktop-search-field .v-field__prepend-inner) {
  padding-left: var(--ds-space-3);
  color: #6b7280;
}

:deep(.desktop-search-field .v-field__input) {
  padding-top: 10px;
  padding-bottom: 10px;
}

:deep(.desktop-search-field .v-field__label) {
  display: none;
}
</style>

<style lang="scss">
.note-dialog-card {
  border-radius: var(--ds-radius-lg) !important;
  overflow: hidden;
}

.note-dialog-header {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2);
  padding: var(--ds-space-5) var(--ds-space-6) var(--ds-space-2);
}

.note-dialog-title {
  font-size: var(--ds-text-md);
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.note-dialog-body {
  padding: var(--ds-space-3) var(--ds-space-6) var(--ds-space-2) !important;
}

.note-textarea .v-field {
  border-radius: var(--ds-radius-md) !important;
}

.note-dialog-actions {
  padding: var(--ds-space-2) var(--ds-space-5) var(--ds-space-4) !important;
}

.small-label-search + .v-menu > .v-overlay__content {
  max-width: 100vw !important;
  width: 100vw !important;
  left: 0 !important;

  > .v-list {
    max-height: 100vh !important;
    width: 100vw !important;
  }
}

/* Desktop: the results panel shares the centered ~1160px container with
   the nav and page content instead of spanning the full viewport */
@media (min-width: 960px) {
  .small-label-search + .v-menu > .v-overlay__content {
    max-width: 1160px !important;
    width: 1160px !important;
    left: 50% !important;
    transform: translateX(-50%);

    > .v-list {
      width: 100% !important;
    }
  }
}
</style>
